if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };

        this.isBundle = this.dataset.isBundle === 'true';
        this.submitEl.addEventListener('click', this.onClick.bind(this));

        const toggleBtn = this.querySelector('.sticky-atc-bar__toggle');
        if (toggleBtn) toggleBtn.addEventListener('click', this.onToggle.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      get wrapperEl() {
        return this.closest('.sticky-atc-bar__wrapper') || this;
      }

      get productImageEl() {
        const parent = this.parentElement;
        return parent ? parent.querySelector('.sticky-atc-bar__product-image') : null;
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.configuratorForm = document.getElementById('computer-configurator-form');

        this.init();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions || this.configuratorForm);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.configuratorPriceListener) {
          document.removeEventListener('configurator:priceUpdate', this.configuratorPriceListener);
        }

        if (this.configuratorChangeListener) {
          document.getElementById('computer-configurator-form')?.removeEventListener('change', this.configuratorChangeListener);
        }

      }

      init() {
        // For bundle products, use configurator form
        if (this.isBundle && this.configuratorForm) {
          this.initBundleProduct();
          return;
        }

        // For regular products, use main product form
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          if (this.productImageEl) this.productImageEl.classList.remove(this.classes.isOpen);
          return;
        }

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle(this.classes.isOpen, isAboveViewport);
              if (this.productImageEl) this.productImageEl.classList.toggle(this.classes.isOpen, isAboveViewport);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      initBundleProduct() {
        // Find the configurator summary section to observe
        const configuratorSummary = this.configuratorForm.querySelector('.configurator-summary');

        if (configuratorSummary) {
          const rootMargin = `${configuratorSummary.offsetHeight}px 0px 0px 0px`;
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                this.classList.toggle(this.classes.isOpen, isAboveViewport);
              if (this.productImageEl) this.productImageEl.classList.toggle(this.classes.isOpen, isAboveViewport);
              });
            },
            { threshold: 1, rootMargin }
          );
          this.observer.observe(configuratorSummary);
        }

        // Listen for configurator price updates
        this.configuratorPriceListener = (event) => {
          this.updatePriceFromConfigurator(event.detail);
          this.updateSpecsFromForm();
        };
        document.addEventListener('configurator:priceUpdate', this.configuratorPriceListener);

        // Update specs on radio change (dispatched manually by configurator card clicks)
        this.configuratorChangeListener = () => this.updateSpecsFromForm();
        document.getElementById('computer-configurator-form')?.addEventListener('change', this.configuratorChangeListener);

        // Set specs on initial load
        this.updateSpecsFromForm();
      }

      onClick() {
        if (this.isBundle && this.configuratorForm) {
          // For bundle products, trigger the configurator form submission
          this.submitEl.setAttribute('aria-disabled', 'true');
          this.submitEl.classList.add('btn--loading');

          // Trigger the configurator form's submit event
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          this.configuratorForm.dispatchEvent(submitEvent);
          return;
        }

        // Regular product behavior
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      onToggle() {
        const isOpen = this.classList.toggle('sticky-atc-bar--open');
        const toggleBtn = this.querySelector('.sticky-atc-bar__toggle');
        if (toggleBtn) toggleBtn.setAttribute('aria-expanded', isOpen);
        if (this.productImageEl) this.productImageEl.classList.toggle('sticky-atc-bar__image--open', isOpen);
        this.moveButton(isOpen);
      }

      moveButton(isOpen) {
        const buttonsEl = this.querySelector('.product-form__buttons');
        const priceContainer = this.querySelector('.sticky-bar-price-container');
        const expandedEl = this.querySelector('.sticky-atc-bar__expanded');
        if (!buttonsEl) return;
        if (isOpen && expandedEl) {
          expandedEl.appendChild(buttonsEl);
        } else if (!isOpen && priceContainer) {
          priceContainer.appendChild(buttonsEl);
        }
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        if (!target) return;

        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }
      }

      formatFeatureTitle(title) {
        if (!title) return title;
        const trimmed = title.trim();
        if (trimmed !== trimmed.toUpperCase()) return trimmed;
        if (!/[A-ZÀ-Ú]/i.test(trimmed)) return trimmed;
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
      }

      updateSpecsFromForm() {
        const form = document.getElementById('computer-configurator-form');
        if (!form) return;
        const groupFeatureMap = {
          processor: 'processador',
          memory: 'ram_memory',
          storage: 'storage',
          graphics: 'graphics_card',
          operating_system: 'operational_system',
          second_storage: 'second_storage',
        };
        Object.entries(groupFeatureMap).forEach(([group, featureKey]) => {
          const checked = form.querySelector(`[data-component-group="${group}"] input[type="radio"]:checked`);
          if (!checked) return;
          const title = checked.closest('.option-card')?.querySelector('.card-title')?.textContent?.trim();
          if (!title) return;
          const row = this.querySelector(`.product-card__product-feature[data-feature-key="${featureKey}"]`);
          if (!row) return;
          // Placeholder options ("Sem sistema operacional", "Sem adicional") hide the row
          const normalized = title.toLowerCase();
          const isPlaceholder = normalized.includes('sem sistema operacional') || normalized.includes('sem adicional');
          row.style.display = isPlaceholder ? 'none' : '';
          if (isPlaceholder) return;
          const valueEl = row.querySelector('.product-card__product-feature-value');
          if (valueEl) valueEl.textContent = this.formatFeatureTitle(title);
        });
      }

      updatePriceFromConfigurator(priceData) {
        const priceContainer = this.querySelector('.sticky-bar-price-container');
        if (!priceContainer) return;

        const { pixPrice, finalPrice, compareAtPrice, bundleDiscount, pixDiscount, installmentCount, installmentValue, showInstallments } = priceData;

        // Helper function to format price
        const formatPrice = (priceInCents) => {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(priceInCents / 100);
        };

        // Build compact price HTML for sticky bar (matching configurator display)
        let html = '<div class="f-price f-price--with-payment-options">';

        // Show compare at price if bundle discount exists (strikethrough)
        if (bundleDiscount > 0 && compareAtPrice > finalPrice) {
          html += `
            <div class="f-price__compare">
              <s class="f-price-item f-price-item--compare">${formatPrice(compareAtPrice)}</s>
            </div>
          `;
        }

        // Main Pix price
        html += `
          <div class="f-price__main">
            <span class="f-price-item f-price-item--pix">${formatPrice(pixPrice)}</span>
          </div>
          <div class="f-price__pix-info text-subtext">
            à vista no pix com ${pixDiscount}% de desconto
          </div>
        `;

        // Show installments if applicable
        if (showInstallments) {
          html += `
            <div class="f-price__installments text-subtext">
              ou ${formatPrice(finalPrice)} em ${installmentCount}x de ${formatPrice(installmentValue)} sem juros no cartão
            </div>
          `;
        }

        html += '</div>';

        const existingButton = priceContainer.querySelector('.product-form__buttons');
        priceContainer.innerHTML = html;
        if (existingButton) priceContainer.appendChild(existingButton);
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper && unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else if (unitPriceWrapper) {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        if (!this.mainProductInfo) return;

        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          this.updatePrice(variant);
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
